import { format, formatDistanceToNowStrict, isDate, isValid, parseISO, set } from 'date-fns';
import { dateFnsLocaleMap, defaultDateFnsLocale } from 'config/date';
import { getBrowserLocale } from 'lib/locale';
import { queryDateFormat } from 'lib/transport';
import logger from 'services/logger';

/**
 * Get locale information object for the specified application locale
 */
const getDateFnsLocale = () => dateFnsLocaleMap.get(getBrowserLocale()) ?? defaultDateFnsLocale;

/**
 * Attempt to parse an ISO8601 date string into a Date object
 *
 * @param {string} value Value to parse
 * @throws {Error} Thrown when the input value cannot be parsed
 */
export const parseStringDate = (value: string) => {
  // Attempt to parse value into a date object
  const date = parseISO(value);

  // Ensure value resulted in a valid date
  if (!isValid(date)) {
    const message = `Failed parsing date (${value})`;
    logger.error('system', message);
    throw new Error(message);
  }

  return date;
};

/**
 * Convert the specified value to a Date object
 *
 * @param {string | Date} value Source ISO8601 date string or a Date object
 */
const getDate = (value: string | Date) =>
  isDate(value) ? (value as Date) : parseStringDate(value as string);

/**
 * Convert the specified ISO8601 date string to the specified format
 *
 * @param {string | Date} value Source ISO8601 date string or a Date object
 * @param {string} pattern Format pattern
 */
const formatDate = (value: string | Date, pattern: string) =>
  format(getDate(value), pattern, {
    locale: getDateFnsLocale(),
  });

/**
 * Convert the specified ISO8601 date string to a short date format
 *
 * @param {string | Date} value Source ISO8601 date string or a Date object
 * @param {boolean} time TRUE to include the time portion
 */
export const formatDateShort = (value: string | Date, time = false) =>
  formatDate(value, time ? 'Pp' : 'P');

/**
 * Convert the specified ISO8601 date string to a medium date format
 *
 * @param {string | Date} value Source ISO8601 date string or a Date object
 * @param {boolean} time TRUE to include the time portion
 */
export const formatDateMedium = (value: string | Date, time = false) =>
  formatDate(value, time ? 'PPp' : 'PP');

/**
 * Convert the specified ISO8601 date string to a long date format
 *
 * @param {string | Date} value Source ISO8601 date string or a Date object
 * @param {boolean} time TRUE to include time portion of the date
 */
export const formatDateLong = (value: string | Date, time = false) =>
  time ? formatDate(value, 'PPPp') : formatDate(value, 'PPP');

/**
 * Convert the specified ISO8601 date string to a relative date format
 *
 * @param {string | Date} value Source ISO8601 date string or a Date object
 */
export const formatDateRelative = (value: string | Date) =>
  formatDistanceToNowStrict(getDate(value), {
    addSuffix: true,
    locale: getDateFnsLocale(),
  });

/**
 * Format a Date object into a string representation used in API requests
 *
 * @param {Date} value Value to format
 */
export const formatQueryDate = (value: Date) => format(value, queryDateFormat);

/**
 * Clear the time portion of the specified Date object
 *
 * @param {Date} date Target date to update
 */
export const clearTime = (date: Date) =>
  set(date, {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
